import React, { useState } from 'react';
import { ArrowLeft, Lock, Key, Shield, Eye, EyeOff, X } from 'lucide-react';
import { useUI } from '../../contexts/UIContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import withTranslation from '../../hoc/withTranslation';

const SecuritySettingsScreen = ({ t, currentLanguage }) => {
  const { navigateToScreen } = useUI();
  const { user, is2FAEnabled, enable2FA, disable2FA } = useAuth();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showDisable2FAConfirm, setShowDisable2FAConfirm] = useState(false);

  const handlePasswordChange = () => {
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError(t('security.fillAllFields', 'Lütfen tüm alanları doldurun'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('security.passwordsDoNotMatch', 'Yeni şifreler eşleşmiyor'));
      return;
    }

    if (newPassword.length < 8) {
      setError(t('security.passwordTooShort', 'Şifre en az 8 karakter olmalıdır'));
      return;
    }

    // Burada normalde API çağrısı yapılır
    setSuccess(t('security.passwordChanged', 'Şifreniz başarıyla değiştirildi'));
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handle2FAToggle = () => {
    if (is2FAEnabled) {
      setShowDisable2FAConfirm(true);
    } else {
      setShow2FAModal(true);
    }
  };

  const handleConfirmDisable2FA = () => {
    disable2FA();
    setShowDisable2FAConfirm(false);
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Header */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 flex items-center shadow-sm`}>
        <button 
          onClick={() => navigateToScreen('profile')}
          className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
        >
          <ArrowLeft className={isDarkMode ? 'text-white' : 'text-gray-800'} size={24} />
        </button>
        <h1 className={`ml-4 text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {t('security.passwordAndSecurity', 'Şifre ve Güvenlik')}
        </h1>
      </div>

      <div className="p-4 space-y-6">
        {/* Şifre Değiştirme */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {t('security.changePassword', 'Şifre Değiştir')}
          </h2>

          <div className="space-y-4">
            {/* Mevcut Şifre */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('security.currentPassword', 'Mevcut Şifre')}
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className={`w-full p-3 rounded-lg border pr-10
                    ${isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'}`}
                  style={{
                    fontSize: 'inherit'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 
                    ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Yeni Şifre */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('security.newPassword', 'Yeni Şifre')}
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={`w-full p-3 rounded-lg border pr-10
                    ${isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'}`}
                  style={{
                    fontSize: 'inherit'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 
                    ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Şifre Tekrar */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('security.confirmPassword', 'Yeni Şifre (Tekrar)')}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full p-3 rounded-lg border pr-10
                    ${isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'}`}
                  style={{
                    fontSize: 'inherit'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 
                    ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-red-500 text-sm">{error}</p>
            )}
            {success && (
              <p className="text-green-500 text-sm">{success}</p>
            )}

            <button
              onClick={handlePasswordChange}
              className={`w-full p-3 rounded-lg font-medium
                ${isDarkMode 
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
            >
              {t('security.updatePassword', 'Şifreyi Güncelle')}
            </button>
          </div>
        </div>

        {/* İki Faktörlü Doğrulama */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {t('security.twoFactorAuth', 'İki Faktörlü Doğrulama')}
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('security.twoFactorDescription', 'Hesabınızı daha güvenli hale getirin')}
              </p>
            </div>
            <button
              onClick={handle2FAToggle}
              className={`px-4 py-2 rounded-lg font-medium
                ${is2FAEnabled 
                  ? (isDarkMode ? 'bg-green-900 text-green-100' : 'bg-green-100 text-green-800')
                  : (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700')
                }`}
            >
              {is2FAEnabled ? t('security.active', 'Aktif') : t('security.inactive', 'Pasif')}
            </button>
          </div>

          {is2FAEnabled && (
            <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
              <div className="flex items-center space-x-2">
                <Shield className={`${isDarkMode ? 'text-green-400' : 'text-green-600'}`} size={20} />
                <p className={`text-sm ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                  {t('security.2FAEnabled', 'İki faktörlü doğrulama etkin')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Güvenlik Günlüğü */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {t('security.securityLog', 'Güvenlik Günlüğü')}
          </h2>

          <div className="space-y-3">
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <div className="flex justify-between items-center">
                <div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {t('security.passwordChanged', 'Şifre değiştirildi')}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    10 Nisan 2024 - 14:30
                  </p>
                </div>
                <Lock className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} size={20} />
              </div>
            </div>

            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <div className="flex justify-between items-center">
                <div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {t('security.2FAActivated', '2FA etkinleştirildi')}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    8 Nisan 2024 - 09:15
                  </p>
                </div>
                <Key className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} size={20} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2FA Etkinleştirme Modal */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 max-w-sm w-full`}>
            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('security.enable2FA', 'İki Faktörlü Doğrulamayı Etkinleştir')}
            </h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('security.enable2FADescription', 'QR kodunu Google Authenticator uygulaması ile tarayın')}
            </p>
            
            <div className="bg-white p-4 rounded-lg mb-4">
              {/* QR Kod placeholder */}
              <div className="w-48 h-48 bg-gray-100 mx-auto flex items-center justify-center">
                <span className="text-gray-500">QR Kod</span>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShow2FAModal(false)}
                className={`flex-1 p-3 rounded-lg font-medium
                  ${isDarkMode 
                    ? 'bg-gray-700 text-white' 
                    : 'bg-gray-200 text-gray-800'}`}
              >
                {t('common.cancel', 'İptal')}
              </button>
              <button
                onClick={() => {
                  enable2FA();
                  setShow2FAModal(false);
                }}
                className={`flex-1 p-3 rounded-lg font-medium
                  ${isDarkMode 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-blue-600 text-white'}`}
              >
                {t('common.enable', 'Etkinleştir')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2FA Disable Confirmation Modal */}
      {showDisable2FAConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg max-w-sm w-full p-6 space-y-4`}>
            <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('security.disable2FAConfirmTitle', 'Disable 2FA')}
            </h3>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('security.disable2FAConfirm', 'İki faktörlü doğrulamayı kapatmak istediğinizden emin misiniz?')}
            </p>
            <div className="flex space-x-3">
              <button 
                className={`flex-1 py-3 rounded-lg font-medium ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                onClick={() => setShowDisable2FAConfirm(false)}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button 
                className={`flex-1 py-3 text-white rounded-lg font-medium ${isDarkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-600 hover:bg-red-700'} transition-all`}
                onClick={handleConfirmDisable2FA}
              >
                {t('common.disable', 'Disable')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default withTranslation(SecuritySettingsScreen);
